import { parseDate } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import clsx from "clsx";
import { CameraIcon, ConstructionIcon } from "lucide-react";

import { Pagination } from "@/components/base/pagination";
import {
  TournamentListFilters,
  tournamentListFilterSchema,
} from "@/components/tournaments/filters";
import type {
  Division,
  Tournament,
  TournamentDivision,
  Venue,
} from "@/db/schema";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { getDefaultTimeZone } from "@/lib/dates";

export const Route = createFileRoute("/tournaments/")({
  component: RouteComponent,
  validateSearch: zodValidator(tournamentListFilterSchema),
  loaderDeps: ({ search }) => search,
  loader: async ({
    deps: {
      page,
      pageSize,
      name,
      divisions,
      venues,
      genders,
      past,
      startDate,
      endDate,
    },
    context: { queryClient, trpc },
  }) => {
    await Promise.all([
      queryClient.ensureQueryData(
        trpc.tournaments.search.queryOptions({
          page,
          pageSize,
          name,
          divisions,
          venues,
          genders,
          past,
          startDate,
          endDate,
        }),
      ),
      queryClient.ensureQueryData(trpc.venues.list.queryOptions()),
    ]);
  },
});

function RouteComponent() {
  const trpc = useTRPC();
  const search = Route.useSearch();

  const {
    page,
    pageSize,
    name,
    divisions,
    venues,
    genders,
    past,
    startDate,
    endDate,
  } = search;

  const { data } = useSuspenseQuery(
    trpc.tournaments.search.queryOptions({
      page,
      pageSize,
      name,
      divisions,
      venues,
      genders,
      past,
      startDate,
      endDate,
    }),
  );

  const { data: tournaments, pageInfo } = data;

  return (
    <DefaultLayout classNames={{ content: "pb-12 space-y-12 w-full relative" }}>
      <div className="scroll-ref w-full bg-slate-300 py-8">
        <TournamentListFilters {...search} />
      </div>
      <div className="mx-auto flex max-w-xl flex-col space-y-6 px-2 pb-6">
        {tournaments.length ? (
          tournaments.map((tournament) => (
            <TournamentListItem key={tournament.id} {...tournament} />
          ))
        ) : (
          <div>
            No tournaments to show. Either update your filters or{" "}
            <Link
              className="text-blue-500 hover:underline"
              to="/tournaments"
              search={{
                page: Math.max(page - 1, 1),
                pageSize,
                divisions,
                venues,
                genders,
                past,
              }}
            >
              go back.
            </Link>
          </div>
        )}

        <Pagination
          to={Route.fullPath}
          page={page}
          pageSize={pageSize}
          pageInfo={pageInfo}
        />
      </div>
    </DefaultLayout>
  );
}

function TournamentListItem({
  id,
  name,
  date,
  venue,
  visible,
  hasGalleryPhotos,
  tournamentDivisions,
}: Pick<Tournament, "id" | "name" | "date" | "visible"> & {
  venue: Pick<Venue, "id" | "name" | "city">;
  hasGalleryPhotos: boolean;
} & {
  tournamentDivisions: (Pick<
    TournamentDivision,
    | "id"
    | "name"
    | "teamSize"
    | "gender"
    | "displayDivision"
    | "displayGender"
    | "status"
  > & {
    division: Pick<Division, "name" | "maxAge" | "display">;
  })[];
}) {
  const parsedDate = parseDate(date);

  const dateFormatter = useDateFormatter({ timeZone: getDefaultTimeZone() });

  return (
    <div className="overflow-hidden rounded-md">
      <div
        className={clsx(
          "w-full px-2 py-1 ",
          visible
            ? "bg-navbar-background text-navbar-foreground"
            : "bg-orange-300 text-content-foreground",
        )}
      >
        <Link
          className="flex w-full flex-col hover:underline"
          to="/tournaments/$tournamentId/"
          params={{ tournamentId: id.toString() }}
        >
          {name && (
            <span className="flex flex-row items-center space-x-2">
              {!visible && <ConstructionIcon size={18} />}
              <span>{name}</span>
            </span>
          )}

          <span className="flex flex-row justify-between font-semibold">
            <span className="flex flex-row items-center gap-2">
              {!visible && !name && <ConstructionIcon size={18} />}
              <span>
                {venue?.name}, {venue?.city}
              </span>
            </span>
            <span
              className={clsx(
                "flex items-center gap-1.5",
                visible ? "text-gray-300" : "text-content-foreground",
              )}
            >
              {hasGalleryPhotos && <CameraIcon size={14} />}
              {dateFormatter.format(parsedDate.toDate(getDefaultTimeZone()))}
            </span>
          </span>
        </Link>
      </div>
      <div className="relative bg-white px-2 py-3">
        <ul>
          {tournamentDivisions?.map(({ id: tdid, status, ...info }) => {
            return (
              <li key={tdid} className="px-2 py-0.5">
                <Link
                  className={clsx(
                    "underline hover:no-underline w-full inline-block",
                    status === "cancelled"
                      ? "text-red-400 line-through"
                      : "text-navbar-background",
                  )}
                  to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                  params={{
                    tournamentId: id.toString(),
                    divisionId: tdid.toString(),
                  }}
                >
                  {getTournamentDivisionDisplay(info)}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
